import { useEffect, useMemo, useState } from 'react';
import { api, type Transaction } from '../lib/api.js';
import { useApp } from '../store/app.js';
import { Money } from '../components/Money.js';
import { Icon, CATEGORY_STYLE, DEFAULT_CATEGORY_STYLE } from '../components/Icon.js';
import { Sheet } from '../components/Sheet.js';
import { Skeleton } from '../components/Skeleton.js';
import { dayHeading, formatFullDate, formatTime, toIso } from '../lib/format.js';
import { t } from '../lib/i18n.js';

type Filter = 'all' | 'expenses' | 'income' | 'transfers' | 'recurring' | 'unusual';

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'הכל' },
  { value: 'expenses', label: 'הוצאות' },
  { value: 'income', label: 'הכנסות' },
  { value: 'transfers', label: 'העברות' },
  { value: 'recurring', label: 'קבועות' },
  { value: 'unusual', label: 'חריגות' },
];

/** Timeline, search and filters. §18 §19 §20 */
export function Transactions() {
  const { accounts, dashboard } = useApp();
  const [rows, setRows] = useState<Transaction[] | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<Transaction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  const today = dashboard?.today ?? toIso(Date.now());

  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(() => {
      setError(null);
      api.transactions
        .list({ filter, q: query || undefined, limit: 100 })
        .then((data) => { if (!cancelled) setRows(data); })
        .catch(() => { if (!cancelled) setError(t.errors.load); });
    }, query ? 260 : 0);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [filter, query, reload]);

  const groups = useMemo(() => groupByDay(rows ?? []), [rows]);
  const accountName = (id: string | null) => accounts.find((a) => a.id === id)?.name ?? '';

  return (
    <>
      <header className="app-header">
        <div className="app-header__bar">
          <span />
          <div className="app-header__title">עסקאות</div>
          <button className="header-btn" onClick={() => setSearching(true)} aria-label="חיפוש">
            <Icon name="search" size={21} />
          </button>
        </div>
        <p className="app-header__sub">כל התנועות שלך במקום אחד</p>
      </header>

      <main className="screen stack">
        <div className="chip-row" role="group" aria-label="סינון">
          {FILTERS.map((item) => (
            <button key={item.value} className="chip" aria-pressed={filter === item.value} onClick={() => setFilter(item.value)}>
              {item.label}
            </button>
          ))}
        </div>

        {query && (
          <div className="between">
            <span className="label">תוצאות עבור “{query}”</span>
            <button className="label--tiny inline-target" onClick={() => setQuery('')}>ניקוי</button>
          </div>
        )}

        {error && (
          <div className="card empty">
            <p className="empty__body">{error}</p>
            <button className="btn" onClick={() => setReload((n) => n + 1)}>{t.common.retry}</button>
          </div>
        )}

        {!rows && !error && (
          <div className="card stack">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={44} />)}
          </div>
        )}

        {rows && rows.length === 0 && (
          <div className="card empty">
            <h2 className="empty__title">עדיין אין כאן תנועות</h2>
            <p className="empty__body">הוסף הוצאה ראשונה כדי להתחיל להבין לאן הכסף הולך.</p>
          </div>
        )}

        {groups.map(([day, items]) => (
          <section className="card" key={day}>
            <h2 className="section-heading" style={{ paddingTop: 0 }}>{dayHeading(day, today)}</h2>
            <div className="list">
              {items.map((tx) => (
                <TransactionRow key={tx.id} tx={tx} accountName={accountName} onSelect={() => setSelected(tx)} />
              ))}
            </div>
          </section>
        ))}
      </main>

      <SearchOverlay open={searching} value={query} onChange={setQuery} onClose={() => setSearching(false)} />
      <TransactionDetail
        transaction={selected}
        onClose={() => setSelected(null)}
        onChanged={() => { setSelected(null); setReload((n) => n + 1); }}
      />
    </>
  );
}

function TransactionRow({
  tx, accountName, onSelect,
}: { tx: Transaction; accountName: (id: string | null) => string; onSelect: () => void }) {
  const { categoryName, categorySlug } = useApp();
  const style = tx.type === 'transfer'
    ? { icon: 'transfer' as const, color: 'var(--c-grey)', soft: 'var(--c-grey-soft)' }
    : CATEGORY_STYLE[categorySlug(tx.categoryId)] ?? DEFAULT_CATEGORY_STYLE;

  return (
    <button className="tx-row" onClick={onSelect}>
      <span className="tx-row__icon" style={{ background: style.soft, color: style.color }}>
        <Icon name={style.icon} size={19} />
      </span>
      <span className="tx-row__body">
        <span className="tx-row__name">{tx.merchant ?? tx.description ?? t.types[tx.type] ?? tx.type}</span>
        <span className="tx-row__meta">
          {tx.type === 'transfer'
            ? `${accountName(tx.accountId)} ← ${accountName(tx.toAccountId)}`
            : `${categoryName(tx.categoryId)} · ${accountName(tx.accountId)}`}
        </span>
      </span>
      <span className="tx-row__side">
        <span className={`tx-row__amount money ${tx.amount > 0 ? 'money--positive' : 'money--negative'}`}>
          <Money value={tx.amount} size="inherit" tone="inherit" />
        </span>
        <span className="tx-row__date">{formatTime(tx.occurredAt)}</span>
      </span>
    </button>
  );
}

/** §19 — structured search that reads like a sentence. */
function SearchOverlay({
  open, value, onChange, onClose,
}: { open: boolean; value: string; onChange: (v: string) => void; onClose: () => void }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { if (open) setDraft(value); }, [open, value]);

  return (
    <Sheet open={open} title="חיפוש" onClose={onClose}>
      <div className="stack">
        <input
          className="input"
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { onChange(draft); onClose(); } }}
          placeholder="וולט, מעל 300, הוצאות מאוגוסט…"
          aria-label="חיפוש תנועות"
        />
        <div className="chip-row">
          {['וולט', 'מעל 300', 'הוצאות מאוגוסט', 'החודש'].map((example) => (
            <button key={example} className="chip" onClick={() => { onChange(example); onClose(); }}>
              {example}
            </button>
          ))}
        </div>
        <p className="label--tiny">
          אפשר לכתוב סכומים (“מעל 300”), חודשים (“אוגוסט”) וסוגים (“הוצאות”) יחד באותה שורה.
        </p>
      </div>
    </Sheet>
  );
}

/** §21 detail and actions */
function TransactionDetail({
  transaction, onClose, onChanged,
}: { transaction: Transaction | null; onClose: () => void; onChanged: () => void }) {
  const { categories, accounts, categoryName, refreshDashboard, refreshAccounts, toast } = useApp();
  const [busy, setBusy] = useState(false);

  if (!transaction) return null;
  const account = accounts.find((a) => a.id === transaction.accountId);

  const recategorise = async (categoryId: string) => {
    setBusy(true);
    try {
      await api.transactions.update(transaction.id, { categoryId, version: transaction.version });
      toast('הקטגוריה עודכנה — נזכור את זה לפעם הבאה');
      await Promise.all([refreshDashboard(), refreshAccounts()]);
      onChanged();
    } catch {
      toast(t.errors.save, { tone: 'danger' });
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.transactions.remove(transaction.id);
      toast('התנועה נמחקה', {
        action: {
          label: t.common.undo,
          run: () => { void api.transactions.restore(transaction.id).then(() => { void refreshDashboard(); onChanged(); }); },
        },
      });
      await Promise.all([refreshDashboard(), refreshAccounts()]);
      onChanged();
    } catch {
      toast(t.errors.save, { tone: 'danger' });
    } finally { setBusy(false); }
  };

  const toggleExcluded = async () => {
    setBusy(true);
    try {
      await api.transactions.update(transaction.id, {
        excludedFromAnalytics: !transaction.excludedFromAnalytics,
        version: transaction.version,
      });
      await refreshDashboard();
      onChanged();
    } finally { setBusy(false); }
  };

  return (
    <Sheet open onClose={onClose} title={transaction.merchant ?? t.types[transaction.type] ?? transaction.type}>
      <div className="stack">
        <div
          className="card"
          style={{ background: 'var(--surface-2)', boxShadow: 'none', textAlign: 'center' }}
        >
          <div style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.02em' }}>
            <Money
              value={transaction.amount}
              size="inherit"
              tone={transaction.amount > 0 ? 'positive' : 'negative'}
              decimals
            />
          </div>
          <span className="label">
            {formatFullDate(transaction.occurredAt)} · {formatTime(transaction.occurredAt)}
          </span>
        </div>

        <div className="card" style={{ background: 'var(--surface-2)', boxShadow: 'none' }}>
          <Row label="חשבון" value={account?.name ?? '—'} />
          <Row label="קטגוריה" value={categoryName(transaction.categoryId)} />
          <Row label="סוג" value={t.types[transaction.type] ?? transaction.type} />
          {transaction.splits.length > 0 && <Row label="פיצול" value={`${transaction.splits.length} חלקים`} />}
          {transaction.excludedFromAnalytics && <Row label="מוחרג מהניתוח" value="כן" />}
        </div>

        <div className="field">
          <span className="field__label">שינוי קטגוריה</span>
          <div className="chip-row">
            {categories
              .filter((c) => c.kind === (transaction.amount > 0 ? 'income' : 'expense'))
              .slice(0, 16)
              .map((c) => (
                <button
                  key={c.id}
                  className="chip"
                  aria-pressed={transaction.categoryId === c.id}
                  disabled={busy}
                  onClick={() => recategorise(c.id)}
                >
                  {c.nameHe ?? c.name}
                </button>
              ))}
          </div>
        </div>

        <button className="btn btn--block" onClick={toggleExcluded} disabled={busy}>
          {transaction.excludedFromAnalytics ? 'החזר לניתוח' : 'החרג מהניתוח'}
        </button>
        <button className="btn btn--danger btn--block" onClick={remove} disabled={busy}>
          {t.common.delete}
        </button>
      </div>
    </Sheet>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="between" style={{ padding: '5px 0' }}>
      <span className="label">{label}</span>
      <span style={{ fontWeight: 600, fontSize: 14 }}>{value}</span>
    </div>
  );
}

function groupByDay(rows: Transaction[]): [string, Transaction[]][] {
  const map = new Map<string, Transaction[]>();
  for (const tx of rows) {
    const day = toIso(tx.occurredAt);
    const list = map.get(day) ?? [];
    list.push(tx);
    map.set(day, list);
  }
  return [...map.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
}
